import { useState, useRef, useEffect } from 'react';
import useScrollReveal from '../useScrollReveal';
import { sectionStyles, getCardClass, getAnimation, hexToRgba, resolveLinkProps } from '../utils';
import SectionDivider from './SectionDivider';
import SectionHeader from './SectionHeader';

const PLATFORM_ICONS = {
    instagram: {
        color: '#E4405F',
        icon: (
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z" />
            </svg>
        ),
    },
    tiktok: {
        color: '#000000',
        icon: (
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-2.88 2.5 2.89 2.89 0 01-2.89-2.89 2.89 2.89 0 012.89-2.89c.28 0 .54.04.79.1v-3.5a6.37 6.37 0 00-.79-.05A6.34 6.34 0 003.15 15.2a6.34 6.34 0 0010.86 4.48V13a8.28 8.28 0 005.58 2.17V11.7a4.83 4.83 0 01-3.77-1.74V6.69h3.77z" />
            </svg>
        ),
    },
    twitter: {
        color: '#1DA1F2',
        icon: (
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
            </svg>
        ),
    },
    facebook: {
        color: '#1877F2',
        icon: (
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
            </svg>
        ),
    },
};

const HeartIcon = () => (
    <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
        <path fillRule="evenodd" d="M3.172 5.172a4 4 0 015.656 0L10 6.343l1.172-1.171a4 4 0 115.656 5.656L10 17.657l-6.828-6.829a4 4 0 010-5.656z" clipRule="evenodd" />
    </svg>
);

const CommentIcon = () => (
    <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
        <path fillRule="evenodd" d="M18 10c0 3.866-3.582 7-8 7a8.841 8.841 0 01-4.083-.98L2 17l1.338-3.123C2.493 12.767 2 11.434 2 10c0-3.866 3.582-7 8-7s8 3.134 8 7zM7 9H5v2h2V9zm8 0h-2v2h2V9zM9 9h2v2H9V9z" clipRule="evenodd" />
    </svg>
);

function PostCard({ item, s, card, visible, delay }) {
    const platform = PLATFORM_ICONS[item.platform] || PLATFORM_ICONS.instagram;
    const borderColor = s.dark ? 'rgba(255,255,255,0.08)' : '#f3f4f6';

    return (
        <div
            className={`${card} rounded-xl border overflow-hidden relative transition-all duration-500 hover:-translate-y-1 hover:shadow-lg ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
            style={{
                borderColor,
                transitionDelay: visible ? `${delay}ms` : '0ms',
            }}
        >
            {/* Header */}
            <div className="flex items-center gap-3 px-4 py-3">
                <div className="w-9 h-9 rounded-full flex items-center justify-center"
                    style={{ backgroundColor: hexToRgba(platform.color, 0.1), color: platform.color }}>
                    {platform.icon}
                </div>
                <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold truncate" style={{ color: s.text }}>{item.author || 'User'}</p>
                    <p className="text-xs truncate" style={{ color: s.textSub }}>{item.handle || '@user'}</p>
                </div>
                <div style={{ color: platform.color }}>{platform.icon}</div>
            </div>

            {/* Image */}
            {item.image_url && (
                <div className="relative">
                    <img src={item.image_url} alt="" className="w-full aspect-square object-cover" loading="lazy" />
                </div>
            )}

            {/* Text */}
            {item.text && (
                <div className="px-4 pt-3">
                    <p className="text-sm leading-relaxed line-clamp-3" style={{ color: s.text }}>{item.text}</p>
                </div>
            )}

            {/* Footer */}
            <div className="flex items-center gap-4 px-4 py-3">
                {item.likes && (
                    <span className="inline-flex items-center gap-1 text-xs" style={{ color: s.textSub }}>
                        <HeartIcon /> {item.likes}
                    </span>
                )}
                {item.comments && (
                    <span className="inline-flex items-center gap-1 text-xs" style={{ color: s.textSub }}>
                        <CommentIcon /> {item.comments}
                    </span>
                )}
                {item.date && (
                    <span className="text-xs ml-auto" style={{ color: s.textSub }}>{item.date}</span>
                )}
            </div>

            {/* Link overlay */}
            {item.link && (
                <a {...resolveLinkProps(item.link)} className="absolute inset-0 z-10" aria-label={`Ver post de ${item.author || 'user'}`} />
            )}
        </div>
    );
}

function TickerCard({ item, s }) {
    const platform = PLATFORM_ICONS[item.platform] || PLATFORM_ICONS.instagram;
    const borderColor = s.dark ? 'rgba(255,255,255,0.08)' : '#f3f4f6';

    return (
        <div className="shrink-0 w-72 rounded-xl border overflow-hidden relative"
            style={{ borderColor, backgroundColor: s.dark ? 'rgba(255,255,255,0.03)' : '#ffffff' }}>
            <div className="flex items-center gap-2.5 px-3.5 py-2.5">
                <div className="w-7 h-7 rounded-full flex items-center justify-center"
                    style={{ backgroundColor: hexToRgba(platform.color, 0.1), color: platform.color }}>
                    {platform.icon}
                </div>
                <div className="min-w-0 flex-1">
                    <p className="text-xs font-semibold truncate" style={{ color: s.text }}>{item.author}</p>
                    <p className="text-[10px] truncate" style={{ color: s.textSub }}>{item.handle}</p>
                </div>
            </div>
            {item.image_url && (
                <img src={item.image_url} alt="" className="w-full aspect-video object-cover" loading="lazy" />
            )}
            {item.text && (
                <p className="text-xs leading-relaxed px-3.5 py-2.5 line-clamp-2" style={{ color: s.text }}>{item.text}</p>
            )}
            <div className="flex items-center gap-3 px-3.5 pb-2.5">
                {item.likes && <span className="inline-flex items-center gap-1 text-[10px]" style={{ color: s.textSub }}><HeartIcon /> {item.likes}</span>}
                {item.comments && <span className="inline-flex items-center gap-1 text-[10px]" style={{ color: s.textSub }}><CommentIcon /> {item.comments}</span>}
            </div>
            {item.link && <a {...resolveLinkProps(item.link)} className="absolute inset-0 z-10" aria-label="Ver post" />}
        </div>
    );
}

export default function SocialFeedSection({ block = {}, primaryColor, secondaryColor, enableAnimations, cardStyle, mediaFolders = {} }) {
    const { config = {}, content = {} } = block;
    const s = sectionStyles(config, primaryColor, secondaryColor);
    const [ref, visible] = useScrollReveal(0.15, !enableAnimations);
    const anim = getAnimation(config, visible);
    const card = getCardClass(cardStyle, s.dark);
    const variant = config.variant || 'wall';
    const columns = config.columns || 3;
    const items = content.items || [];

    const cls = `${s.padClass} relative ${s.radiusClass} ${s.shadowClass}`;
    const bgS = s.bgStyle.background ? s.bgStyle : { backgroundColor: s.bg || '#ffffff', ...s.bgStyle };

    const colClass = columns === 2 ? 'sm:columns-2' : columns === 4 ? 'sm:columns-2 lg:columns-4' : 'sm:columns-2 lg:columns-3';

    // Carousel state
    const scrollRef = useRef(null);
    const [canScrollLeft, setCanScrollLeft] = useState(false);
    const [canScrollRight, setCanScrollRight] = useState(true);

    const checkScroll = () => {
        const el = scrollRef.current;
        if (!el) return;
        setCanScrollLeft(el.scrollLeft > 10);
        setCanScrollRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 10);
    };

    useEffect(() => {
        if (variant === 'carousel') {
            const el = scrollRef.current;
            if (el) {
                el.addEventListener('scroll', checkScroll, { passive: true });
                checkScroll();
                return () => el.removeEventListener('scroll', checkScroll);
            }
        }
    }, [variant]);

    const scrollCarousel = (dir) => {
        const el = scrollRef.current;
        if (el) el.scrollBy({ left: dir * 320, behavior: 'smooth' });
    };

    // Empty state
    if (items.length === 0) {
        return (
            <section className={cls} style={bgS}>
                <SectionDivider type={config.divider_top} color="transparent" position="top"  config={config}/>
                <div ref={ref} className={`${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                    <SectionHeader tag={content.tag} title={content.title} subtitle={content.subtitle} config={config} s={s} />
                    <div className="text-center py-12">
                        <div className="w-16 h-16 mx-auto rounded-2xl flex items-center justify-center mb-4"
                            style={{ backgroundColor: hexToRgba(s.accent, 0.08) }}>
                            <svg className="w-8 h-8" style={{ color: s.accent }} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M7 4V2m0 2a2 2 0 00-2 2v1a2 2 0 002 2h0a2 2 0 002-2V6a2 2 0 00-2-2zm10 0V2m0 2a2 2 0 00-2 2v1a2 2 0 002 2h0a2 2 0 002-2V6a2 2 0 00-2-2zM4 8h16M4 8v10a2 2 0 002 2h12a2 2 0 002-2V8" />
                            </svg>
                        </div>
                        <p className="text-sm" style={{ color: s.textSub }}>Conecta tus redes sociales</p>
                    </div>
                </div>
                <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom"  config={config}/>
            </section>
        );
    }

    // CTA button
    const FollowCta = () => {
        if (!content.cta_text || !content.profile_url) return null;
        return (
            <div className="text-center mt-10">
                <a {...resolveLinkProps(content.profile_url)}
                    className="inline-flex items-center gap-2 px-6 py-3 rounded-full text-sm font-semibold transition-all hover:scale-105 hover:shadow-lg"
                    style={{ backgroundColor: s.accent, color: '#ffffff' }}>
                    {content.cta_text}
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                    </svg>
                </a>
            </div>
        );
    };

    // Ticker variant — auto-scrolling horizontal strip
    if (variant === 'ticker') {
        const doubled = [...items, ...items];
        return (
            <section className={cls} style={bgS}>
                <SectionDivider type={config.divider_top} color="transparent" position="top"  config={config}/>
                <div ref={ref} className={`${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                    <SectionHeader tag={content.tag} title={content.title} subtitle={content.subtitle} config={config} s={s} />
                </div>
                <div className="overflow-hidden group">
                    <div className="flex gap-4 py-2 animate-[socialTicker_30s_linear_infinite] group-hover:[animation-play-state:paused]"
                        style={{ width: 'max-content' }}>
                        {doubled.map((item, i) => (
                            <TickerCard key={i} item={item} s={s} />
                        ))}
                    </div>
                </div>
                <div className={`${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8`}>
                    <FollowCta />
                </div>
                <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom"  config={config}/>
                <style>{`
                    @keyframes socialTicker {
                        0% { transform: translateX(0); }
                        100% { transform: translateX(-50%); }
                    }
                `}</style>
            </section>
        );
    }

    // Carousel variant — horizontal scroll with arrows
    if (variant === 'carousel') {
        return (
            <section className={cls} style={bgS}>
                <SectionDivider type={config.divider_top} color="transparent" position="top"  config={config}/>
                <div ref={ref} className={`${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                    <SectionHeader tag={content.tag} title={content.title} subtitle={content.subtitle} config={config} s={s} />

                    <div className="relative">
                        {/* Arrows */}
                        {canScrollLeft && (
                            <button onClick={() => scrollCarousel(-1)}
                                className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-3 z-10 w-10 h-10 rounded-full shadow-lg flex items-center justify-center transition-all hover:scale-110"
                                style={{ backgroundColor: s.dark ? 'rgba(255,255,255,0.15)' : '#ffffff', color: s.text }}>
                                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                                </svg>
                            </button>
                        )}
                        {canScrollRight && (
                            <button onClick={() => scrollCarousel(1)}
                                className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-3 z-10 w-10 h-10 rounded-full shadow-lg flex items-center justify-center transition-all hover:scale-110"
                                style={{ backgroundColor: s.dark ? 'rgba(255,255,255,0.15)' : '#ffffff', color: s.text }}>
                                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                                </svg>
                            </button>
                        )}

                        <div ref={scrollRef}
                            className="flex gap-5 overflow-x-auto snap-x snap-mandatory scrollbar-hide pb-2"
                            style={{ scrollbarWidth: 'none' }}>
                            {items.map((item, i) => (
                                <div key={i} className="shrink-0 w-72 snap-start relative">
                                    <PostCard item={item} s={s} card={card} visible={visible} delay={i * 100} />
                                </div>
                            ))}
                        </div>
                    </div>

                    <FollowCta />
                </div>
                <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom"  config={config}/>
            </section>
        );
    }

    // Wall variant (default) — masonry-style grid
    return (
        <section className={cls} style={bgS}>
            <SectionDivider type={config.divider_top} color="transparent" position="top"  config={config}/>
            <div ref={ref} className={`${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                <SectionHeader tag={content.tag} title={content.title} subtitle={content.subtitle} config={config} s={s} />

                <div className={`columns-1 ${colClass} gap-5 [&>div]:mb-5 [&>div]:break-inside-avoid`}>
                    {items.map((item, i) => (
                        <div key={i} className="relative">
                            <PostCard item={item} s={s} card={card} visible={visible} delay={i * 80} />
                        </div>
                    ))}
                </div>

                <FollowCta />
            </div>
            <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom"  config={config}/>
        </section>
    );
}
